/**
 * Query parameters that app links carry over from the landing URL.
 *
 * Only attribution data is forwarded. Tool-internal parameters must stay on the
 * landing: e.g. Reddit's Event Setup Tool opens the site with `reddit_open_est=true`
 * and the Reddit pixel on beta.lucidbook.com starts the tool whenever it sees that
 * parameter. App links open in a new tab without `window.opener`, so the tool then
 * fails with "Connection failed … cross-origin opener policy". The same applies to
 * GTM preview (`gtm_debug`) and similar parameters.
 */
const FORWARDED_PARAMS = new Set([
  // Click IDs of ad platforms
  'rdt_cid', // Reddit — read by the app's first-touch attribution
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'ttclid',
  'twclid',
  'msclkid',
  'li_fat_id',
  // Referral code, read by the app's /register page
  'ref',
  'referral_code',
]);

/** True for query parameters the landing forwards to the app (all `utm_*` + FORWARDED_PARAMS). */
export function isForwardedParam(name: string): boolean {
  const normalized = name.toLowerCase();
  return normalized.startsWith('utm_') || FORWARDED_PARAMS.has(normalized);
}

function decodedParamName(pair: string): string {
  const rawName = pair.split('=', 1)[0];
  try {
    return decodeURIComponent(rawName.replace(/\+/g, ' '));
  } catch {
    return rawName;
  }
}

/** Keeps only forwarded parameters, with their original encoding, order and repetitions. */
export function filterForwardedQuery(search: string): string {
  return search
    .replace(/^\?/, '')
    .split('&')
    .filter((pair) => pair !== '' && isForwardedParam(decodedParamName(pair)))
    .join('&');
}

/** Forward the landing's attribution parameters to app links, preserving the target path and hash. */
export function buildAppUrl(
  destination: string,
  search = typeof window === 'undefined' ? '' : window.location.search
): string {
  const query = filterForwardedQuery(search);
  if (!query) return destination;

  const url = new URL(destination);
  if (url.origin !== 'https://beta.lucidbook.com') return destination;

  // The current landing values take precedence if the target already has these keys.
  new URLSearchParams(query).forEach((_, name) => url.searchParams.delete(name));
  const targetQuery = url.search.slice(1);
  url.search = targetQuery ? `${targetQuery}&${query}` : query;

  return url.toString();
}
